import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { 
  Ticket,
  Plus, 
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  MapPin,
  Filter,
  Loader2,
  ChevronRight,
  MessageSquare
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Open', icon: AlertCircle, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  resolved: { label: 'Resolved', icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-500/10' },
  closed: { label: 'Closed', icon: XCircle, color: 'text-gray-500', bg: 'bg-gray-500/10' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-gray-400', bg: 'bg-gray-400/10' },
  medium: { label: 'Medium', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  high: { label: 'High', color: 'text-orange-500', bg: 'bg-orange-500/10' },
  critical: { label: 'Critical', color: 'text-red-500', bg: 'bg-red-500/10' },
};

export function TicketPanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  onNavigateToTicket,
  title = "Active Issues"
}) {
  const { token, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    capturePosition: true
  });

  // Use refs to always have latest values (avoid closure issues)
  const currentSweepRef = useRef(currentSweep);
  const cameraPositionRef = useRef(cameraPosition);
  
  useEffect(() => {
    currentSweepRef.current = currentSweep;
  }, [currentSweep]);
  
  useEffect(() => {
    cameraPositionRef.current = cameraPosition;
  }, [cameraPosition]);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/tickets/tour/${currentTour.id}`
        : `${API_URL}/api/tickets/tour/${currentTour.id}?status=${statusFilter}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTickets(data);
      }
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token, statusFilter]);

  useEffect(() => {
    if (isOpen) {
      fetchTickets();
    }
  }, [isOpen, fetchTickets]);

  // Create ticket
  const handleCreateTicket = async () => {
    if (!newTicket.title || !currentTour?.id) return;
    
    // Use refs to get the latest values (avoid closure issues)
    const latestSweep = currentSweepRef.current;
    const latestCameraPosition = cameraPositionRef.current;
    
    setCreating(true);
    try {
      const ticketData = {
        tour_id: currentTour.id,
        title: newTicket.title,
        description: newTicket.description,
        priority: newTicket.priority,
        category: newTicket.category || null,
        status: 'open'
      };

      // Capture current 3D position if enabled
      if (newTicket.capturePosition && latestSweep?.id) {
        ticketData.sweep_id = latestSweep.id;
        if (latestCameraPosition?.position) {
          ticketData.position = latestCameraPosition.position;
        }
        if (latestCameraPosition?.rotation) {
          ticketData.rotation = latestCameraPosition.rotation;
        }
      }

      const res = await fetch(`${API_URL}/api/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(ticketData)
      });

      if (res.ok) {
        const created = await res.json();
        setTickets(prev => [created, ...prev]);
        setIsCreateDialogOpen(false);
        setNewTicket({ title: '', description: '', priority: 'medium', category: '', capturePosition: true });
      } else {
        const error = await res.json();
        console.error('Ticket creation failed:', error);
      }
    } catch (err) {
      console.error('Failed to create ticket:', err);
    } finally {
      setCreating(false);
    }
  };

  // Navigate to ticket location
  const handleNavigate = (ticket) => {
    if (ticket.sweep_id && onNavigateToTicket) {
      // Close the dialog first, then navigate
      setSelectedTicket(null);
      // Small delay to ensure dialog closes before navigation
      setTimeout(() => {
        onNavigateToTicket(ticket.sweep_id, { 
          rotation: ticket.rotation,
          transitionTime: 1500 
        });
      }, 100);
    }
  };

  const filteredTickets = tickets;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px] flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            {title}
            <Badge variant="secondary" className="ml-2">{tickets.length}</Badge>
          </SheetTitle>
          <SheetDescription>
            Track issues and inspections for {currentTour?.name || 'this tour'}.
          </SheetDescription>
        </SheetHeader>

        {/* Toolbar */}
        <div className="flex items-center gap-2 py-4 border-b">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          
          {canEdit && (
            <Button onClick={() => setIsCreateDialogOpen(true)} className="ml-auto">
              <Plus className="h-4 w-4 mr-2" />
              New Ticket
            </Button>
          )}
        </div>

        {/* Viewer mode notice */}
        {isViewer && (
          <Alert className="mb-4">
            <AlertDescription className="text-xs">
              You have view-only access. Contact an admin to create or edit tickets.
            </AlertDescription>
          </Alert>
        )}

        {/* Tickets List */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-8">
              <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No tickets yet</p>
              {canEdit && (
                <p className="text-xs text-muted-foreground mt-1">
                  Create a ticket to track an issue or inspection.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2 py-2">
              {filteredTickets.map((ticket) => {
                const status = STATUS_CONFIG[ticket.status];
                const priority = PRIORITY_CONFIG[ticket.priority];
                const StatusIcon = status.icon;
                
                return (
                  <div
                    key={ticket.id}
                    className="p-3 rounded-lg border bg-card hover:bg-secondary/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn("p-2 rounded-lg", status.bg)}>
                        <StatusIcon className={cn("h-4 w-4", status.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{ticket.title}</p>
                          <Badge variant="outline" className={cn("text-[10px]", priority.color)}>
                            {priority.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {ticket.description || 'No description'}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          {ticket.sweep_id && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              Position saved
                            </span>
                          )}
                          {ticket.comments?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {ticket.comments.length}
                            </span>
                          )}
                          <span>{ticket.created_by_name}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Create Ticket Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Ticket</DialogTitle>
              <DialogDescription>
                Report an issue or schedule an inspection.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="ticket-title">Title *</Label>
                <Input
                  id="ticket-title"
                  placeholder="e.g., Corrosion detected on valve V-101"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ticket-desc">Description</Label>
                <Textarea
                  id="ticket-desc"
                  placeholder="Describe the issue in detail..."
                  value={newTicket.description}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={newTicket.priority}
                    onValueChange={(value) => setNewTicket(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={newTicket.category}
                    onValueChange={(value) => setNewTicket(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="safety">Safety</SelectItem>
                      <SelectItem value="inspection">Inspection</SelectItem>
                      <SelectItem value="defect">Defect</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Position capture */}
              <div className="p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Link to 3D Position</span>
                  </div>
                  <Button
                    variant={newTicket.capturePosition ? "default" : "outline"}
                    size="sm"
                    onClick={() => setNewTicket(prev => ({ ...prev, capturePosition: !prev.capturePosition }))}
                  >
                    {newTicket.capturePosition ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
                {newTicket.capturePosition && currentSweep?.id && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Current position will be saved: {currentSweep.id.substring(0, 12)}...
                  </p>
                )}
                {newTicket.capturePosition && !currentSweep?.id && (
                  <p className="text-xs text-yellow-500 mt-2">
                    Navigate to a position in the 3D viewer first.
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTicket} disabled={!newTicket.title || creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Ticket
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ticket Detail Dialog */}
        <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="sm:max-w-lg">
            {selectedTicket && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <Badge className={cn(STATUS_CONFIG[selectedTicket.status].bg, STATUS_CONFIG[selectedTicket.status].color)}>
                      {STATUS_CONFIG[selectedTicket.status].label}
                    </Badge>
                    <Badge variant="outline" className={PRIORITY_CONFIG[selectedTicket.priority].color}>
                      {PRIORITY_CONFIG[selectedTicket.priority].label}
                    </Badge>
                  </div>
                  <DialogTitle className="mt-2">{selectedTicket.title}</DialogTitle>
                  <DialogDescription>
                    Created by {selectedTicket.created_by_name}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {selectedTicket.description && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Description</Label>
                      <p className="mt-1 text-sm">{selectedTicket.description}</p>
                    </div>
                  )}

                  {selectedTicket.sweep_id && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => handleNavigate(selectedTicket)}
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Go to Location
                    </Button>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

export default TicketPanel;
